package one.devsbox.www.ui.company

import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import compose.assets.Svg
import compose.assets.svg.Plus
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.www_portal.generated.resources.Res
import one.devsbox.www.www_portal.generated.resources.ui_company_listing_btn_add_company
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState

enum class DialogMode {
    Actions,
    Rename,
    Delete
}

@VM
class ListingCompanyVM : NavigationViewModel() {
    private lateinit var ctx: ListingCompanyCtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    var state by mutableStateOf<DataState<SharedIO.Companies>>(DataState.Loading())
        private set

    var loadingMore by mutableStateOf(false)
        private set

    val search = TextFieldState()

    // Dialog state
    var activeCompany by mutableStateOf<SharedIO.CompanySimple?>(null)
        private set
    var dialogMode by mutableStateOf(DialogMode.Actions)
        private set
    var dialogLoading by mutableStateOf(false)
        private set
    var dialogError by mutableStateOf<String?>(null)
        private set
    var renameName by mutableStateOf("")

    var showCreateDialog by mutableStateOf(false)
        private set
    var createName by mutableStateOf("")

    interface Action {
        fun loadMore()
        fun reload()
        fun openCompanyActions(company: SharedIO.CompanySimple)
        fun closeCompanyActions()
        fun setDialogMode(mode: DialogMode)
        fun executeSwitch()
        fun executeRename()
        fun executeDelete()
        fun openCreateDialog()
        fun closeCreateDialog()
        fun executeCreate()
        fun back()
    }

    val action = object : Action {
        override fun loadMore() {
            val currentState = state
            if (currentState is DataState.Result && !loadingMore && currentState.nnvl.items.size < currentState.nnvl.count) {
                fetchCompanies(page = currentState.nnvl.page + 1)
            }
        }

        override fun reload() {
            fetchCompanies(0)
        }

        override fun openCompanyActions(company: SharedIO.CompanySimple) {
            activeCompany = company
            dialogMode = DialogMode.Actions
            dialogLoading = false
            dialogError = null
            renameName = company.name
        }

        override fun closeCompanyActions() {
            activeCompany = null
        }

        override fun setDialogMode(mode: DialogMode) {
            dialogMode = mode
            dialogError = null
            if (mode == DialogMode.Rename) {
                renameName = activeCompany?.name ?: ""
            }
        }

        override fun executeSwitch() {
            val company = activeCompany ?: return
            dialogLoading = true
            dialogError = null
            scope.launch {
                runCatching {
                    Repository.ads.user.switchCompany(company.id)
                }.onSuccess {
                    dialogLoading = false
                    activeCompany = null
                    window.location.reload()
                }.onFailure {
                    dialogLoading = false
                    dialogError = it.message ?: "Failed to switch company"
                }
            }
        }

        override fun executeRename() {
            val company = activeCompany ?: return
            val newName = renameName.trim()
            if (newName.isEmpty()) {
                dialogError = "Name cannot be empty"
                return
            }
            dialogLoading = true
            dialogError = null
            scope.launch {
                runCatching {
                    Repository.ads.company.rename(company.id, newName)
                }.onSuccess {
                    dialogLoading = false
                    activeCompany = null
                    reload()
                }.onFailure {
                    dialogLoading = false
                    dialogError = it.message ?: "Failed to rename company"
                }
            }
        }

        override fun executeDelete() {
            val company = activeCompany ?: return
            dialogLoading = true
            dialogError = null
            scope.launch {
                runCatching {
                    Repository.ads.company.delete(company.id)
                }.onSuccess {
                    dialogLoading = false
                    activeCompany = null
                    reload()
                }.onFailure {
                    dialogLoading = false
                    dialogError = it.message ?: "Failed to delete company"
                }
            }
        }

        override fun openCreateDialog() {
            createName = ""
            dialogLoading = false
            dialogError = null
            showCreateDialog = true
        }

        override fun closeCreateDialog() {
            showCreateDialog = false
        }

        override fun executeCreate() {
            val name = createName.trim()
            if (name.isEmpty()) {
                dialogError = "Name cannot be empty"
                return
            }
            dialogLoading = true
            dialogError = null
            scope.launch {
                runCatching {
                    Repository.ads.company.add(name)
                }.onSuccess {
                    dialogLoading = false
                    showCreateDialog = false
                    reload()
                }.onFailure {
                    dialogLoading = false
                    dialogError = it.message ?: "Failed to create company"
                }
            }
        }

        override fun back() {
            ctx.nav.up()
        }
    }

    fun setup(ctx: ListingCompanyCtx) {
        this.ctx = ctx
        fetchCompanies(0)
    }

    private fun fetchCompanies(page: Int) {
        if (page == 0) state = DataState.Loading()
        else loadingMore = true

        scope.launch {
            runCatching {
                Repository.ads.company.listing(page)
            }.onSuccess { response ->
                if (page == 0) {
                    state = DataState.Result(response)
                } else {
                    val current = (state as? DataState.Result)?.nnvl
                    if (current != null) {
                        state = DataState.Result(
                            current.copy(
                                page = response.page,
                                items = current.items + response.items
                            )
                        )
                    }
                }
                loadingMore = false
                setupActions()
            }.onFailure {
                if (page == 0) state = DataState.Error(it.message ?: "Unknown error")
                loadingMore = false
                contentActions = emptyList()
            }
        }
    }

    private fun setupActions() {
        contentActions = buildList {
            if (ctx.me.permission.company.write) {
                add(
                    ScreenContentAction(
                        text = Res.string.ui_company_listing_btn_add_company,
                        color = ctx.colorPrimaryContainer,
                        icon = Svg.Plus
                    ) {
                        action.openCreateDialog()
                    }
                )
            }
        }
    }

    override fun onRelease() {}
}
